/*
  Reviews from Amazon, drawn in the theme's own design.

  Amazon's "Reviews from Amazon" app (Buy with Prime) serves each mapped
  product's Amazon rating and reviews as HTML from cdn.elements.amazon, which
  allows this store's origin (CORS). Its own blocks draw that HTML inside a
  closed shadow root the theme cannot style, and bring ~1.8 MB of script. So
  the theme reads the same two endpoints itself and draws the figures into
  the product page's existing rating line (.zp-rating) and the "What people
  say" cards (zh-testi, as snippets/judgeme-review-card).

  Markup hooks (sections/zen-product, sections/pp-product,
  snippets/pp-product-reviews, snippets/brand-product-card):
    [data-amz-rating]   the rating line; [data-amz-rating-text] is its text
    [data-amz-reviews]  the reviews band; [data-zp-reviews-track] gets the cards
    [data-amz-card]     a product card's rating row (snippets/brand-product-card)
  Each carries data-amz-target / -sku / -gid (the app's target id and the
  product's first variant). A product the app has not mapped answers 400, and
  the page keeps what the theme rendered.

  Remote text is only ever set with textContent; DOMParser documents never
  run scripts.
*/
(function () {
  if (window.__amzReviews) { return; }
  window.__amzReviews = true;

  var BASE = 'https://cdn.elements.amazon/elements/';
  var MAX_REVIEWS = 10;
  var MAX_PAGES = 4;
  var memo = {};

  function query(el, extra) {
    var p = new URLSearchParams({
      targetId: el.getAttribute('data-amz-target') || '',
      'product-sku': el.getAttribute('data-amz-sku') || '',
      'product-external-id': el.getAttribute('data-amz-gid') || ''
    });
    if (extra) { Object.keys(extra).forEach(function (k) { p.set(k, extra[k]); }); }
    return p.toString();
  }

  function getDoc(path, el, extra) {
    return fetch(BASE + path + '?' + query(el, extra), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) { throw new Error('amazon ' + r.status); } return r.text(); })
      .then(function (t) { return new DOMParser().parseFromString(t, 'text/html'); });
  }

  /* One request per product per visit: shared between callers, and kept
     in sessionStorage so the next page view paints straight away. */
  function cached(key, load) {
    if (memo[key]) { return memo[key]; }
    try {
      var hit = sessionStorage.getItem(key);
      if (hit) { return (memo[key] = Promise.resolve(JSON.parse(hit))); }
    } catch (e) {}
    memo[key] = load().then(function (d) {
      try { sessionStorage.setItem(key, JSON.stringify(d)); } catch (e) {}
      return d;
    });
    memo[key].catch(function () { delete memo[key]; });
    return memo[key];
  }

  function num(s) { var n = parseFloat(String(s || '').replace(/,/g, '')); return isNaN(n) ? 0 : n; }

  /* Resolves to { rating, count }, or { none: true } for a product the app
     has not linked to a listing (Amazon answers 400). That answer is kept
     too, so a product grid doesn't ask again on every page. */
  function loadRating(el) {
    return cached('amz:r:' + el.getAttribute('data-amz-gid'), function () {
      return fetch(BASE + 'amazon-product-rating/rating?' + query(el), { credentials: 'omit' }).then(function (r) {
        if (r.status === 400 || r.status === 404) { return { none: true }; }
        if (!r.ok) { throw new Error('amazon ' + r.status); }
        return r.text().then(function (t) {
          var doc = new DOMParser().parseFromString(t, 'text/html');
          var stars = doc.querySelector('.star-rating[aria-label]');
          var count = doc.querySelector('.rating-text.ember-bold') || doc.querySelector('.rating-count');
          var d = {
            rating: stars ? num(stars.getAttribute('aria-label')) : 0,
            count: count ? num(count.textContent.replace(/[^\d,]/g, '')) : 0
          };
          return d.rating > 0 && d.count > 0 ? d : { none: true };
        });
      });
    }).then(function (d) { return d.none ? null : d; });
  }

  function loadReviews(el) {
    return cached('amz:v:' + el.getAttribute('data-amz-gid'), function () {
      var out = [];
      function page(token, n) {
        return getDoc('amazon-product-reviews/review-list', el, { 'next-token': token }).then(function (doc) {
          doc.querySelectorAll('.single-review-container').forEach(function (c) {
            if (out.length >= MAX_REVIEWS) { return; }
            var body = c.querySelector('.revealer-review-text');
            var text = body ? (body.getAttribute('content') || '').trim() : '';
            var stars = c.querySelector('.star-rating[aria-label]');
            var rating = stars ? Math.round(num(stars.getAttribute('aria-label'))) : 0;
            if (!text || rating < 1 || rating > 5) { return; }
            var at = c.querySelector('.reviewed-at');
            var date = at ? at.textContent.replace(/^.*\son\s/, '').trim() : '';
            var name = c.querySelector('.customer');
            var title = c.querySelector('.review-title');
            out.push({
              name: (name && name.textContent.trim()) || 'Amazon Customer',
              rating: rating,
              title: title ? title.textContent.trim() : '',
              body: text,
              date: date,
              verified: !!c.querySelector('.verified-purchase')
            });
          });
          var next = doc.querySelector('#next-button[data-next-token]');
          var token2 = next ? next.getAttribute('data-next-token') : '';
          if (out.length < MAX_REVIEWS && token2 && n + 1 < MAX_PAGES) { return page(token2, n + 1); }
          if (!out.length) { throw new Error('amazon: no reviews'); }
          return out;
        });
      }
      return page('', 0);
    });
  }

  function ratingLabel(d) {
    return d.rating.toFixed(1) + ' · ' + d.count.toLocaleString('en-US') + (d.count === 1 ? ' review' : ' reviews');
  }

  function show(el) { el.style.visibility = ''; }

  function paintRating(el, d) {
    var stars = el.querySelector('.zp-stars');
    var fill = el.querySelector('.zp-stars-fill');
    var text = el.querySelector('[data-amz-rating-text]');
    if (stars) { stars.setAttribute('aria-label', 'Rated ' + d.rating.toFixed(1) + ' out of 5 stars'); }
    if (fill) { fill.style.width = Math.min(100, d.rating / 5 * 100) + '%'; }
    if (text) { text.textContent = ratingLabel(d); }
  }

  function node(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  function fmtDate(s) {
    var t = Date.parse(s);
    return isNaN(t) ? s : new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /* Same markup as snippets/judgeme-review-card.liquid with source: 'amazon'. */
  function card(r) {
    var a = node('article', 'zh-testi');
    if (r.verified) { a.appendChild(node('span', 'zh-testi-verified', '✓ Verified')); }
    var st = node('div', 'zh-stars zh-stars--sm');
    st.setAttribute('role', 'img');
    st.setAttribute('aria-label', 'Rated ' + r.rating + ' out of 5');
    for (var i = 1; i <= 5; i++) {
      if (i <= r.rating) { st.appendChild(document.createTextNode('★')); }
      else { st.appendChild(node('span', 'zh-star-off', '★')); }
    }
    a.appendChild(st);
    if (r.title) { a.appendChild(node('h3', 'zh-testi-title', r.title)); }
    a.appendChild(node('p', 'zh-testi-body', r.body));
    var who = node('div', 'zh-testi-who');
    var av = node('span', 'zh-testi-avatar');
    var ini = node('span', 'zh-testi-initials', r.name.charAt(0).toUpperCase());
    ini.setAttribute('aria-hidden', 'true');
    av.appendChild(ini);
    var meta = node('span');
    meta.appendChild(node('span', 'zh-testi-name', r.name));
    meta.appendChild(node('span', 'zh-testi-meta', 'Review from Amazon' + (r.date ? ' · ' + fmtDate(r.date) : '')));
    who.appendChild(av);
    who.appendChild(meta);
    a.appendChild(who);
    return a;
  }

  function paintReviews(band, list, rating) {
    var track = band.querySelector('[data-zp-reviews-track]');
    if (!track) { return; }
    track.replaceChildren.apply(track, list.map(card));
    track.scrollLeft = 0;
    band.querySelectorAll('[data-amz-hide-when-loaded]').forEach(function (n) { n.remove(); });
    band.querySelectorAll('[data-amz-show-when-loaded]').forEach(function (n) { n.hidden = false; });
    var sum = band.querySelector('[data-amz-summary]');
    if (sum && rating) {
      var star = node('span', 'zp-reviews-source-star', '★');
      star.setAttribute('aria-hidden', 'true');
      sum.replaceChildren(star, document.createTextNode(' ' + ratingLabel(rating)));
      sum.closest('[hidden]') && (sum.closest('[hidden]').hidden = false);
    }
    band.classList.remove('zp-reviews--empty');
    track.dispatchEvent(new Event('scroll'));
    if (window.__zpReviewScan) { window.__zpReviewScan(); }
  }

  function afterLoad(fn) {
    if (document.readyState === 'complete') { fn(); } else { window.addEventListener('load', fn, { once: true }); }
  }

  /* A figure already fetched this visit paints at once. Otherwise the
     request waits for the page's load event, so a new connection to
     Amazon never competes with the main image on a phone. */
  function initRating(el) {
    if (el.__amz) { return; } el.__amz = true;
    var hit = null;
    try { hit = JSON.parse(sessionStorage.getItem('amz:r:' + el.getAttribute('data-amz-gid')) || 'null'); } catch (e) {}
    function run() { loadRating(el).then(function (d) { if (d) { paintRating(el, d); } }, function () {}).then(function () { show(el); }); }
    if (hit) { run(); } else { afterLoad(run); }
  }

  /* Product card rating row (snippets/brand-product-card): fetched once the
     card nears the viewport, after the page has loaded. The row's space is
     kept either way; it only becomes visible with a figure to show. */
  function paintCard(el, d) {
    var stars = el.querySelector('.zh-card-stars');
    if (stars) {
      var full = Math.round(d.rating);
      stars.setAttribute('aria-label', 'Rated ' + d.rating.toFixed(1) + ' out of 5');
      stars.replaceChildren();
      for (var i = 1; i <= 5; i++) {
        if (i <= full) { stars.appendChild(document.createTextNode('★')); }
        else { stars.appendChild(node('span', 'zh-card-star-off', '★')); }
      }
    }
    var score = el.querySelector('.zh-card-score');
    var count = el.querySelector('.zh-card-count');
    if (score) { score.textContent = d.rating.toFixed(1); }
    if (count) { count.textContent = '(' + d.count.toLocaleString('en-US') + ')'; }
  }

  var cardIO = null;
  function runCard(el) {
    loadRating(el).then(function (d) {
      if (d) { paintCard(el, d); show(el); }
      else if (el.hasAttribute('data-amz-has-own')) { show(el); }
    }, function () { if (el.hasAttribute('data-amz-has-own')) { show(el); } });
  }
  function initCard(el) {
    if (el.__amz) { return; } el.__amz = true;
    afterLoad(function () {
      if (!('IntersectionObserver' in window)) { runCard(el); return; }
      if (!cardIO) {
        cardIO = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { cardIO.unobserve(e.target); runCard(e.target); } });
        }, { rootMargin: '400px 0px 400px 0px' });
      }
      cardIO.observe(el);
    });
  }

  function initBand(band) {
    if (band.__amz) { return; } band.__amz = true;
    function go() {
      Promise.all([loadReviews(band), loadRating(band).catch(function () { return null; })])
        .then(function (r) { paintReviews(band, r[0], r[1]); }, function () {});
    }
    /* The pages come one after another (each needs the previous page's
       token), so start as soon as the page has loaded rather than waiting for
       the band to scroll near. Never before the load event: on a phone the
       collapsed sections below the fold put the band "near" at once, and the
       review fetches then competed with the main product image. */
    afterLoad(go);
  }

  function scan() {
    document.querySelectorAll('[data-amz-rating]').forEach(initRating);
    document.querySelectorAll('[data-amz-reviews]').forEach(initBand);
    document.querySelectorAll('[data-amz-card]').forEach(initCard);
  }
  scan();
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', scan); }
  document.addEventListener('brand:defer:open', scan);
})();
